import React, { useRef, useState, forwardRef } from 'react';
import Meta from '@/components/Meta';
import { useApp } from '@/context/AppContext';
import HTMLFlipBook from 'react-pageflip';
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';

// A single page component for the flipbook
const Page = forwardRef((props, ref) => {
  return (
    <div className="page bg-white border border-gray-200 shadow-sm flex flex-col justify-center items-center relative overflow-hidden" ref={ref}>
      <div className="page-content w-full h-full p-8 flex flex-col justify-center items-center">
        {props.image ? (
          <img src={props.image} alt={`Page ${props.number}`} className="max-w-full max-h-full object-contain pointer-events-none" />
        ) : (
          <div className="text-center">
            <h2 className="text-4xl font-bold text-[#0c1b44] mb-4">Page {props.number}</h2>
            <p className="text-gray-500">Placeholder for Catalog Image</p>
          </div>
        )}
      </div>
      <div className="page-footer absolute bottom-4 w-full text-center text-xs text-gray-400">
        - {props.number} -
      </div>
    </div>
  );
});

const CatalogPage = () => {
  const { settings } = useApp();
  const bookRef = useRef();
  const [page, setPage] = useState(0);
  const [totalPage, setTotalPage] = useState(0);
  
  // Provide your actual catalog images here
  const catalogImages = Array.from({ length: 12 }, (_, i) => `/catalog_pages/page_${i + 1}.jpg`);

  const onPage = (e) => {
    setPage(e.data);
  };

  const nextButtonClick = () => {
    if (bookRef.current) bookRef.current.pageFlip().flipNext();
  };

  const prevButtonClick = () => {
    if (bookRef.current) bookRef.current.pageFlip().flipPrev();
  };

  const onInit = (e) => {
    if (bookRef.current) {
        setTotalPage(bookRef.current.pageFlip().getPageCount());
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center py-8 px-4">
      <Meta 
        title="Product Catalog" 
        description="View our latest Grand Palace General Trading product catalog in a 3D flipbook." 
        url="https://gpgt.ae/catalog"
      />
      <div className="max-w-6xl w-full">
        <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-[#0c1b44] mb-2">Digital Product Catalog</h1>
            <p className="text-gray-500 max-w-2xl mx-auto">
                Flip through our comprehensive range of products. You can click on the corners of the pages or drag them to turn.
            </p>
        </div>

        {/* Toolbar */}
        <div className="flex justify-between items-center bg-white p-4 rounded-t-xl shadow-sm border border-gray-200 border-b-0 max-w-4xl mx-auto">
            <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={prevButtonClick} disabled={page === 0}>
                    <ChevronLeft className="h-4 w-4 mr-1" /> Prev
                </Button>
                <Button variant="outline" size="sm" onClick={nextButtonClick} disabled={page >= totalPage - 1}>
                    Next <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
            </div>
            <div className="text-sm font-medium text-gray-500">
                Page {page + 1} of {totalPage || catalogImages.length}
            </div>
            <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => window.open('/GPGT_Profile.pdf', '_blank')}>
                    <Download className="h-4 w-4 mr-1" /> Download PDF
                </Button>
            </div>
        </div>

        {/* Flipbook Container */}
        <div className="bg-white p-6 md:p-12 rounded-b-xl shadow-sm border border-gray-200 flex justify-center overflow-hidden max-w-4xl mx-auto">
            <HTMLFlipBook
                width={400}
                height={550}
                size="stretch"
                minWidth={315}
                maxWidth={500}
                minHeight={400}
                maxHeight={700}
                maxShadowOpacity={0.5}
                showCover={true}
                mobileScrollSupport={true}
                onFlip={onPage}
                onInit={onInit}
                className="flip-book"
                ref={bookRef}
                usePortrait={true} // Switches to 1-page mode on small screens automatically
            >
                {catalogImages.map((img, i) => (
                    <Page key={i} number={i + 1} image={img} />
                ))}
            </HTMLFlipBook>
        </div>

        <div className="mt-12 text-center text-sm text-gray-400">
            Need a printed copy? Contact us at <a href={`mailto:${settings?.company_email || 'sales@gpgt.ae'}`} className="text-[#0c1b44] hover:underline">{settings?.company_email || 'sales@gpgt.ae'}</a>
        </div>
      </div>
    </div>
  );
};

export default CatalogPage;
